import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeViewer = ({ experiment, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('implementation');
  const [copied, setCopied] = useState(false);

  const tabs = [
    { id: 'implementation', name: 'Implementation', icon: 'Code' },
    { id: 'shaders', name: 'Shaders', icon: 'Zap' },
    { id: 'documentation', name: 'Docs', icon: 'FileText' }
  ];

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !experiment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-6xl h-[80vh] glass-panel rounded-xl overflow-hidden cosmic-glow">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/20">
          <div>
            <h2 className="font-headline font-bold text-xl text-foreground">
              {experiment?.title}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Technical Implementation & Documentation
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-border/20">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 font-body font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-accent border-b-2 border-accent bg-accent/5' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.name}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'implementation' && (
            <div className="h-full flex">
              {/* Code Panel */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-border/20">
                  <div className="flex items-center space-x-2">
                    <Icon name="FileCode" size={16} className="text-accent" />
                    <span className="font-accent text-sm text-foreground">main.js</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(experiment?.code?.implementation)}
                    iconName={copied ? "Check" : "Copy"}
                    iconPosition="left"
                    className="text-muted-foreground hover:text-accent"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <div className="flex-1 overflow-auto p-4 bg-surface/20">
                  <pre className="font-accent text-sm text-foreground">
                    <code>{experiment?.code?.implementation}</code>
                  </pre>
                </div>
              </div>

              {/* Info Panel */}
              <div className="w-80 border-l border-border/20 p-4 overflow-auto">
                <h3 className="font-headline font-bold text-foreground mb-3">
                  Technical Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-body font-medium text-foreground mb-2">
                      Technologies Used
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {experiment?.technologies?.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md font-accent"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-body font-medium text-foreground mb-2">
                      Key Features
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {experiment?.features?.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <Icon name="ChevronRight" size={14} className="text-accent mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-body font-medium text-foreground mb-2">
                      Performance Notes
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {experiment?.performanceNotes}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'shaders' && (
            <div className="h-full p-4 overflow-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="Zap" size={20} className="text-accent" />
                    <span>Vertex Shader</span>
                  </h3>
                  <div className="bg-surface/20 rounded-lg p-4">
                    <pre className="font-accent text-sm text-foreground overflow-auto">
                      <code>{experiment?.code?.vertexShader}</code>
                    </pre>
                  </div>
                </div>
                <div>
                  <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="Palette" size={20} className="text-cosmic-energy" />
                    <span>Fragment Shader</span>
                  </h3>
                  <div className="bg-surface/20 rounded-lg p-4">
                    <pre className="font-accent text-sm text-foreground overflow-auto">
                      <code>{experiment?.code?.fragmentShader}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documentation' && (
            <div className="h-full p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-invert max-w-none">
                  <h3 className="font-headline font-bold text-foreground mb-4">
                    {experiment?.title} - Technical Documentation
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-body font-medium text-foreground mb-2">Overview</h4>
                      <p className="text-muted-foreground">
                        {experiment?.documentation?.overview}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-body font-medium text-foreground mb-2">Implementation Approach</h4>
                      <p className="text-muted-foreground">
                        {experiment?.documentation?.approach}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-body font-medium text-foreground mb-2">Challenges & Solutions</h4>
                      <ul className="space-y-2 text-muted-foreground">
                        {experiment?.documentation?.challenges?.map((challenge, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                            <span>{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-body font-medium text-foreground mb-2">Future Improvements</h4>
                      <ul className="space-y-2 text-muted-foreground">
                        {experiment?.documentation?.improvements?.map((improvement, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="Lightbulb" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                            <span>{improvement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeViewer;